import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import React, { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';

import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Sidebar,
  sidebarConfig,
  SidebarExpandButton,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { SidebarSearchModal } from '@backstage/plugin-search';

import { Box, Link, makeStyles } from '@material-ui/core';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BrushIcon from '@material-ui/icons/Brush';
import BugReportIcon from '@material-ui/icons/BugReport';
import CloudIcon from '@material-ui/icons/Cloud';
import DirectionsIcon from '@material-ui/icons/Directions';
import EmojiObjects from '@material-ui/icons/EmojiObjects';
import ExtensionIcon from '@material-ui/icons/Extension';
import AutoAwesomeIcon from '@material-ui/icons/Flare';
import GitHubIcon from '@material-ui/icons/GitHub';
import GroupIcon from '@material-ui/icons/Group';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import ListIcon from '@material-ui/icons/List';
import MemoryIcon from '@material-ui/icons/Memory';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import SearchIcon from '@material-ui/icons/Search';
import TimelineIcon from '@material-ui/icons/Timeline';

import { AIReadinessLogo } from '../home/AIReadinessLogo';
import { APILogo } from '../home/APILogo';
import { ArgoCDBlackLogo } from '../home/ArgoCDLogo';
import { KubernetesLogo } from '../home/KubernetesLogo';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { SidebarAccordionGroup } from './SidebarAccordionGroup';
import { UserSidebarItem } from './UserSidebarItem';

sidebarConfig.drawerWidthOpen = 240;

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    paddingLeft: 24,
    paddingTop: 16,
    paddingBottom: 16,
    boxSizing: 'border-box',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
  },
  svg: {
    width: '120px',
    height: '24px',
  },
});

const useBottomRowStyles = makeStyles(theme => ({
  border: {
    borderTop: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.12)'
    }`,
    width: '100%',
  },
  rowExpanded: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  userWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  expandWrapper: {
    flexShrink: 0,
    width: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowCollapsed: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
}));

const BottomRow = () => {
  const classes = useBottomRowStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <Box className={classes.border}>
      {isOpen ? (
        <Box className={classes.rowExpanded}>
          <Box className={classes.userWrapper}>
            <UserSidebarItem />
          </Box>
          <Box className={classes.expandWrapper}>
            <SidebarExpandButton />
          </Box>
        </Box>
      ) : (
        <Box className={classes.rowCollapsed}>
          <SidebarExpandButton />
          <UserSidebarItem />
        </Box>
      )}
    </Box>
  );
};

const SidebarLogo = () => {
  const { root, link, svg } = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={link}
        aria-label="Home"
      >
        {isOpen ? <LogoFull cssClasses={{ svg }} /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  return (
    <SidebarPage>
      <Sidebar disableExpandOnHover>
        <SidebarLogo />
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid rgba(0,0,0,0.12)',
            width: '100%',
            margin: '2px 0',
          }}
        />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid rgba(0,0,0,0.12)',
            width: '100%',
            margin: '2px 0',
          }}
        />
        <SidebarItem icon={HomeIcon} to="/" text="Home" title="" />
        <SidebarAccordionGroup icon={<LocalLibraryIcon />} label="Catalogs">
          <SidebarItem
            icon={ExtensionIcon}
            to="applications"
            text="Applications"
            title=""
          />
          <SidebarItem icon={KubernetesLogo} to="kaap" text="KaaP" title="" />
          <SidebarItem icon={ArgoCDBlackLogo} to="daas" text="DaaS" title="" />
          <SidebarItem
            icon={CloudIcon}
            to="subscriptions"
            text="Subscriptions"
            title=""
          />
          <SidebarItem
            icon={KubernetesLogo}
            to="kubernetes"
            text="Kubernetes"
            title=""
          />
          <SidebarItem icon={APILogo} to="api-docs" text="APIs" title="" />
          <SidebarItem
            icon={MemoryIcon}
            to="ai-tools"
            text="AI Inventory"
            title=""
          />
          <SidebarItem
            icon={GitHubIcon}
            to="repositories"
            text="Repositories"
            title=""
          />
          <SidebarItem
            icon={BugReportIcon}
            to="jira-teams"
            text="Jira Teams"
            title=""
          />
          <SidebarItem
            icon={NotificationsActiveIcon}
            to="pagerduty-teams"
            text="PagerDuty Teams"
            title=""
          />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" title="" />
          <SidebarItem
            icon={GroupIcon}
            to="supervisory-groups"
            text="Teams"
            title=""
          />
          <SidebarItem icon={GroupIcon} to="users" text="Users" title="" />
          <SidebarItem
            icon={ListIcon}
            to="catalog-list"
            text="All catalogs"
            title=""
          />
        </SidebarAccordionGroup>
        <SidebarAccordionGroup icon={<TimelineIcon />} label="Dashboards">
          <SidebarItem
            to="/engineering-insights"
            text="Insights"
            icon={TimelineIcon}
            title=""
          />
          <SidebarItem
            to="/scorecards"
            text="Scorecards"
            icon={AssessmentIcon}
            title=""
          />
          <SidebarItem
            to="/all-scorecards"
            text="All Scorecards"
            icon={AssessmentIcon}
            title=""
          />
          <SidebarItem
            to="ai-coding-readiness"
            text="AI Readiness"
            icon={AIReadinessLogo}
            title=""
          />
        </SidebarAccordionGroup>
        <SidebarItem
          icon={CreateComponentIcon}
          to="create?filters[kind]=template&filters[user]=all"
          text="Self-service..."
          title=""
        />
        <SidebarItem
          icon={DirectionsIcon}
          to="tech-radar"
          text="Tech Radar"
          title=""
        />
        <SidebarItem
          icon={EmojiObjects}
          to="tomtom-lab"
          text="What The Hack"
          title=""
        />
        <SidebarItem
          icon={AutoAwesomeIcon}
          to="ai-library"
          text="AI Library"
          title=""
        />
        <FeatureFlagged with="show-accentuate-sidebar">
          <SidebarScrollWrapper>
            <SidebarItem
              icon={BrushIcon}
              to="accentuate"
              text="Accentuate"
              title=""
            />
          </SidebarScrollWrapper>
        </FeatureFlagged>
        <SidebarSpace />
        <BottomRow />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
